import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { adminService } from '../../services/adminService';
import { Exam, QuestionOption, TrainingModule } from '../../types/training';

export const ExamManagement: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();
  const [modules, setModules] = useState<TrainingModule[]>([]);
  const [selectedModule, setSelectedModule] = useState<TrainingModule | null>(null);
  const [exam, setExam] = useState<Exam | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingExam, setLoadingExam] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateExam, setShowCreateExam] = useState(false);
  const [showAddQuestion, setShowAddQuestion] = useState(false);

  const loadModules = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminService.getModules();
      setModules(data);
      // Keep the selected module in sync with the latest data (e.g. after creating an exam).
      // Without this, `selectedModule.examId` can be stale until a reload/re-select.
      setSelectedModule((prev) => {
        const idToSelect = moduleId || prev?.id;
        if (!idToSelect) return prev;
        return data.find((m) => m.id === idToSelect) || null;
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load modules');
    } finally {
      setLoading(false);
    }
  }, [moduleId]);

  useEffect(() => {
    loadModules();
  }, [loadModules]);

  useEffect(() => {
    setExam(null);
    if (selectedModule?.examId) {
      setLoadingExam(true);
      loadExam(selectedModule.examId);
    } else {
      setLoadingExam(false);
    }
  }, [selectedModule]);

  const loadExam = async (examId: string) => {
    try {
      const data = await adminService.getExam(examId);
      setExam(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load exam');
    } finally {
      setLoadingExam(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!exam || !window.confirm('Are you sure you want to delete this question?')) return;

    try {
      await adminService.deleteQuestion(exam.id, questionId);
      loadExam(exam.id);
    } catch (err: any) {
      alert(err.message || 'Failed to delete question');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <button
        onClick={() => navigate('/admin')}
        className="text-blue-600 hover:text-blue-800 mb-4 flex items-center"
      >
        <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to Dashboard
      </button>
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Exam Management</h2>
        <p className="mt-1 text-sm text-gray-600">Create and manage exams for training modules</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Module Selection */}
      <div className="bg-white shadow rounded-lg p-6">
        <label htmlFor="module" className="block text-sm font-medium text-gray-700 mb-2">
          Select Training Module
        </label>
        <select
          id="module"
          value={selectedModule?.id || ''}
          onChange={(e) => {
            const module = modules.find((m) => m.id === e.target.value);
            setSelectedModule(module || null);
          }}
          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
        >
          <option value="">Select a module...</option>
          {modules.map((module) => (
            <option key={module.id} value={module.id}>
              {module.title} {module.examId ? '(Has Exam)' : ''}
            </option>
          ))}
        </select>
      </div>

      {selectedModule && loadingExam && (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      )}

      {selectedModule && !loadingExam && !exam && (
        <div className="bg-white shadow rounded-lg p-6 text-center">
          <p className="text-gray-600 mb-4">This module doesn't have an exam yet.</p>
          <button
            onClick={() => setShowCreateExam(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            Create Exam
          </button>
        </div>
      )}

      {exam && (
        <div className="space-y-6">
          {/* Exam Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{exam.title}</h3>
                <p className="text-sm text-gray-600 mt-1">Pass Rate: {exam.passRate}%</p>
              </div>
              <button
                onClick={() => setShowAddQuestion(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Question
              </button>
            </div>

            {/* Questions List */}
            <div className="mt-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Questions ({exam.questions?.length || 0})
              </h4>
              {!exam.questions || exam.questions.length === 0 ? (
                <p className="text-sm text-gray-500">No questions added yet</p>
              ) : (
                <div className="space-y-4">
                  {exam.questions
                    .sort((a, b) => a.order - b.order)
                    .map((question, index) => (
                      <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex justify-between items-start mb-3">
                          <h5 className="text-sm font-medium text-gray-900">
                            Question {index + 1}
                          </h5>
                          <button
                            onClick={() => handleDeleteQuestion(question.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          </button>
                        </div>
                        <p className="text-sm text-gray-700 mb-3">{question.questionText}</p>
                        <div className="space-y-2">
                          {question.options.map((option) => (
                            <div
                              key={option.id}
                              className={`flex items-center p-2 rounded ${
                                option.id === question.correctOptionId
                                  ? 'bg-green-50 border border-green-200'
                                  : 'bg-gray-50'
                              }`}
                            >
                              <span className="text-sm text-gray-700">{option.text}</span>
                              {option.id === question.correctOptionId && (
                                <span className="ml-auto text-xs text-green-600 font-medium">
                                  Correct
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Create Exam Modal */}
      {showCreateExam && selectedModule && (
        <CreateExamModal
          module={selectedModule}
          onClose={() => setShowCreateExam(false)}
          onSuccess={() => {
            setShowCreateExam(false);
            loadModules();
          }}
        />
      )}

      {/* Add Question Modal */}
      {showAddQuestion && exam && (
        <AddQuestionModal
          exam={exam}
          onClose={() => setShowAddQuestion(false)}
          onSuccess={() => {
            setShowAddQuestion(false);
            loadExam(exam.id);
          }}
        />
      )}
    </div>
  );
};

interface CreateExamModalProps {
  module: TrainingModule;
  onClose: () => void;
  onSuccess: () => void;
}

const CreateExamModal: React.FC<CreateExamModalProps> = ({ module, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: `${module.title} - Exam`,
    passRate: 70,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    try {
      setLoading(true);
      await adminService.createExam({
        moduleId: module.id,
        ...formData,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to create exam');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Create Exam</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700">
              Exam Title
            </label>
            <input
              type="text"
              id="title"
              required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <label htmlFor="passRate" className="block text-sm font-medium text-gray-700">
              Pass Rate (%)
            </label>
            <input
              type="number"
              id="passRate"
              required
              min="0"
              max="100"
              value={formData.passRate}
              onChange={(e) => setFormData({ ...formData, passRate: parseInt(e.target.value) })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Exam'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface AddQuestionModalProps {
  exam: Exam;
  onClose: () => void;
  onSuccess: () => void;
}

const AddQuestionModal: React.FC<AddQuestionModalProps> = ({ exam, onClose, onSuccess }) => {
  const [questionText, setQuestionText] = useState('');
  const [options, setOptions] = useState<Array<{ text: string; isCorrect: boolean }>>([
    { text: '', isCorrect: false },
    { text: '', isCorrect: false },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addOption = () => {
    if (options.length < 6) {
      setOptions([...options, { text: '', isCorrect: false }]);
    }
  };

  const removeOption = (index: number) => {
    if (options.length > 2) {
      setOptions(options.filter((_, i) => i !== index));
    }
  };

  const updateOption = (index: number, text: string) => {
    const newOptions = [...options];
    newOptions[index].text = text;
    setOptions(newOptions);
  };

  const setCorrectOption = (index: number) => {
    const newOptions = options.map((opt, i) => ({
      ...opt,
      isCorrect: i === index,
    }));
    setOptions(newOptions);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const correctIndex = options.findIndex((opt) => opt.isCorrect);
    if (correctIndex === -1) {
      setError('Please select a correct answer');
      return;
    }

    if (options.some((opt) => !opt.text.trim())) {
      setError('All options must have text');
      return;
    }

    try {
      setLoading(true);
      const questionOptions: QuestionOption[] = options.map((opt, i) => ({
        id: `opt-${i}`,
        text: opt.text,
      }));

      await adminService.addQuestion(exam.id, {
        questionText,
        options: questionOptions,
        correctOptionId: questionOptions[correctIndex].id,
        order: (exam.questions?.length || 0) + 1,
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message || 'Failed to add question');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium text-gray-900">Add Question</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="question" className="block text-sm font-medium text-gray-700">
              Question Text
            </label>
            <textarea
              id="question"
              required
              rows={3}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>

          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Answer Options (2-6)
              </label>
              {options.length < 6 && (
                <button
                  type="button"
                  onClick={addOption}
                  className="text-sm text-blue-600 hover:text-blue-900"
                >
                  + Add Option
                </button>
              )}
            </div>
            <div className="space-y-2">
              {options.map((option, index) => (
                <div key={index} className="flex items-center space-x-2">
                  <input
                    type="radio"
                    name="correct"
                    checked={option.isCorrect}
                    onChange={() => setCorrectOption(index)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                  />
                  <input
                    type="text"
                    required
                    value={option.text}
                    onChange={(e) => updateOption(index, e.target.value)}
                    placeholder={`Option ${index + 1}`}
                    className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                  />
                  {options.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(index)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-2 text-xs text-gray-500">Select the radio button for the correct answer</p>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Adding...' : 'Add Question'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
